import { useMemo, useReducer, useState } from "react";
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import type { ConnectedClient } from "@/api/schemas/sites";

type ExportRow = Record<string, string | number | boolean | null | undefined>;

type ClientFilterState = {
  onlineOnly: boolean;
  heavyOnly: boolean;
  edgeControllersOnly: boolean;
  wiredOnly: boolean;
  clientFilter: string;
  statusFilter: string;
  ipFilter: string;
  ssidFilter: string;
  lastSeenFilter: string;
  usageFilter: string;
};

function buildClientColumns({
  clientFilter,
  statusFilter,
  ipFilter,
  ssidFilter,
  lastSeenFilter,
  usageFilter,
  setFilters,
  timeAgo,
  fmtBytes,
  isWiredClient,
  wiredDetails,
}: {
  clientFilter: string;
  statusFilter: string;
  ipFilter: string;
  ssidFilter: string;
  lastSeenFilter: string;
  usageFilter: string;
  setFilters: (p: Partial<ClientFilterState>) => void;
  timeAgo: (iso: string | null) => string;
  fmtBytes: (value: number) => string;
  isWiredClient: (client: ConnectedClient) => boolean;
  wiredDetails: (client: ConnectedClient) => string;
}): DataTableColumn<ConnectedClient>[] {
  return [
    {
      key: "client",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Client</span>
          <Input
            value={clientFilter}
            onChange={(e) => setFilters({ clientFilter: e.target.value })}
            placeholder="Name or MAC"
            className="h-8"
          />
        </div>
      ),
      width: "minmax(220px, 1.3fr)",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => (
        <div>
          <div className="truncate text-sm font-semibold text-fg">{r.name}</div>
          <div className="font-mono text-[10.5px] text-fg-mute">
            {r.mac}
            {r.manufacturer ? ` · ${r.manufacturer}` : ""}
            {r.os ? ` · ${r.os}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Status</span>
          <Input
            value={statusFilter}
            onChange={(e) => setFilters({ statusFilter: e.target.value })}
            placeholder="Online/Offline"
            className="h-8"
          />
        </div>
      ),
      width: "100px",
      render: (r) =>
        r.online ? (
          <Badge variant="ok" size="sm">Online</Badge>
        ) : (
          <Badge variant="neutral" size="sm">Offline</Badge>
        ),
    },
    {
      key: "ip",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">IP</span>
          <Input
            value={ipFilter}
            onChange={(e) => setFilters({ ipFilter: e.target.value })}
            placeholder="IP contains"
            className="h-8"
          />
        </div>
      ),
      width: "150px",
      render: (r) => (
        <span className="font-mono text-[11px] text-fg-dim">{r.ip ?? "-"}</span>
      ),
    },
    {
      key: "ssid",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">SSID / VLAN</span>
          <Input
            value={ssidFilter}
            onChange={(e) => setFilters({ ssidFilter: e.target.value })}
            placeholder="SSID contains"
            className="h-8"
          />
        </div>
      ),
      width: "1fr",
      render: (r) => (
        <div>
          <div className="text-sm text-fg">
            {isWiredClient(r) ? "Wired" : (r.ssid ?? "-")}
          </div>
          <div className="font-mono text-[10.5px] text-fg-mute">
            {isWiredClient(r)
              ? wiredDetails(r)
              : r.vlanOrPort
                ? `VLAN ${r.vlanOrPort}`
                : "-"}
          </div>
        </div>
      ),
    },
    {
      key: "lastSeen",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Last seen</span>
          <Input
            value={lastSeenFilter}
            onChange={(e) => setFilters({ lastSeenFilter: e.target.value })}
            placeholder="Contains"
            className="h-8"
          />
        </div>
      ),
      width: "150px",
      sortable: true,
      sortValue: (r) => r.lastSeen,
      render: (r) => (
        <span className="text-xs text-fg-dim">{timeAgo(r.lastSeen)}</span>
      ),
    },
    {
      key: "usage",
      label: (
        <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
          <span className="font-mono uppercase tracking-[0.1em]">Usage (last hour)</span>
          <Input
            value={usageFilter}
            onChange={(e) => setFilters({ usageFilter: e.target.value })}
            placeholder="e.g. 120 MB"
            className="h-8"
          />
        </div>
      ),
      width: "200px",
      align: "right",
      sortable: true,
      sortValue: (r) => r.bytesDown + r.bytesUp,
      render: (r) => {
        const total = r.bytesDown + r.bytesUp;
        const pct = Math.min(100, (total / 2_500_000_000) * 100);
        return (
          <div className="flex flex-col items-end gap-0.5">
            <div className="text-sm font-semibold text-fg">{fmtBytes(total)}</div>
            <div className="h-1 w-32 overflow-hidden rounded-full bg-line/40">
              <div
                className={cn(
                  "h-full rounded-full",
                  total > 200_000_000 ? "bg-peach" : "bg-cyan",
                )}
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
            <div className="font-mono text-[10px] text-fg-mute">
              ↓ {fmtBytes(r.bytesDown)} · ↑ {fmtBytes(r.bytesUp)}
            </div>
          </div>
        );
      },
    },
  ];
}

function connectedDeviceName(client: ConnectedClient): string | null {
  const candidate = client as ConnectedClient & Record<string, unknown>;
  const fields = [
    "connectedDeviceName",
    "deviceName",
    "connectedToDevice",
    "switchName",
    "switchDeviceName",
  ];
  for (const key of fields) {
    const value = candidate[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function isWiredClient(client: ConnectedClient): boolean {
  const ssid = (client.ssid ?? "").toLowerCase();
  const vlanOrPort = (client.vlanOrPort ?? "").toLowerCase();
  return ssid === "wired" || vlanOrPort.startsWith("port");
}

function wiredDetails(client: ConnectedClient): string {
  const portLabel = client.vlanOrPort ? client.vlanOrPort : "Wired";
  const deviceName = connectedDeviceName(client);
  if (deviceName) return `${portLabel} on ${deviceName}`;
  return portLabel;
}

export function ConnectedClientsTable({
  clients,
  totalShown,
  range,
  refreshing,
  onRefresh,
  rangeLabel,
  timeAgo,
  fmtBytes,
  exportRowsCsv,
  exportRowsXlsx,
}: {
  clients: ConnectedClient[];
  totalShown: number;
  range: string;
  refreshing: boolean;
  onRefresh: () => void;
  rangeLabel: (rangeId: string) => string;
  isWithinRange: (iso: string | null | undefined, rangeId: string) => boolean;
  timeAgo: (iso: string | null) => string;
  fmtBytes: (value: number) => string;
  exportRowsCsv: (rows: ExportRow[], fileName: string) => void;
  exportRowsXlsx: (rows: ExportRow[], fileName: string) => Promise<void>;
}) {
  const [
    { onlineOnly, heavyOnly, edgeControllersOnly, wiredOnly, clientFilter, statusFilter, ipFilter, ssidFilter, lastSeenFilter, usageFilter },
    setFilters,
  ] = useReducer(
    (s: ClientFilterState, p: Partial<ClientFilterState>) => ({ ...s, ...p }),
    { onlineOnly: true, heavyOnly: false, edgeControllersOnly: false, wiredOnly: false, clientFilter: "", statusFilter: "", ipFilter: "", ssidFilter: "", lastSeenFilter: "", usageFilter: "" },
  );
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const filtered = useMemo(
    () =>
      clients.filter((client) => {
        const clientQ = clientFilter.trim().toLowerCase();
        const statusQ = statusFilter.trim().toLowerCase();
        const ipQ = ipFilter.trim().toLowerCase();
        const ssidQ = ssidFilter.trim().toLowerCase();
        const lastSeenQ = lastSeenFilter.trim().toLowerCase();
        const usageQ = usageFilter.trim().toLowerCase();
        const usageText = fmtBytes(
          client.bytesDown + client.bytesUp,
        ).toLowerCase();

        if (onlineOnly && !client.online) return false;
        if (heavyOnly && client.bytesDown + client.bytesUp < 100_000_000)
          return false;
        if (wiredOnly && !isWiredClient(client)) return false;
        if (
          edgeControllersOnly &&
          !client.name.toLowerCase().startsWith("android-e818")
        ) {
          return false;
        }
        if (
          clientQ &&
          !(
            client.name.toLowerCase().includes(clientQ) ||
            client.mac.toLowerCase().includes(clientQ)
          )
        ) {
          return false;
        }
        if (statusQ) {
          const statusText = client.online ? "online" : "offline";
          if (!statusText.includes(statusQ)) return false;
        }
        if (ipQ && !(client.ip ?? "").toLowerCase().includes(ipQ)) return false;
        if (ssidQ && !(client.ssid ?? "").toLowerCase().includes(ssidQ)) {
          return false;
        }
        if (
          lastSeenQ &&
          !timeAgo(client.lastSeen).toLowerCase().includes(lastSeenQ) &&
          !(client.lastSeen ?? "").toLowerCase().includes(lastSeenQ)
        ) {
          return false;
        }
        if (usageQ && !usageText.includes(usageQ)) return false;
        return true;
      }),
    [
      clients,
      clientFilter,
      edgeControllersOnly,
      onlineOnly,
      heavyOnly,
      wiredOnly,
      ipFilter,
      lastSeenFilter,
      ssidFilter,
      statusFilter,
      usageFilter,
      fmtBytes,
      timeAgo,
    ],
  );

  const cols = buildClientColumns({
    clientFilter,
    statusFilter,
    ipFilter,
    ssidFilter,
    lastSeenFilter,
    usageFilter,
    setFilters,
    timeAgo,
    fmtBytes,
    isWiredClient,
    wiredDetails,
  });

  const clientExportRows = useMemo(
    () =>
      filtered.map((client) => ({
        Client: client.name,
        MAC: client.mac,
        Manufacturer: client.manufacturer ?? "",
        OS: client.os ?? "",
        Status: client.online ? "online" : "offline",
        IP: client.ip ?? "",
        SSID: isWiredClient(client) ? "Wired" : (client.ssid ?? ""),
        VLAN: client.vlanOrPort ?? "",
        "Last Seen": client.lastSeen ?? "",
        "Bytes Down": client.bytesDown,
        "Bytes Up": client.bytesUp,
      })),
    [filtered],
  );

  function downloadClientsCsv() {
    exportRowsCsv(clientExportRows, "connected-clients");
  }

  async function downloadClientsXlsx() {
    setExportingXlsx(true);
    try {
      await exportRowsXlsx(clientExportRows, "connected-clients");
    } finally {
      setExportingXlsx(false);
    }
  }

  return (
    <Card className="mb-4">
      <CardContent className="pb-10">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <CardTitle>Connected clients</CardTitle>
            <CardSub>
              Clients connected to the site within the {rangeLabel(range)}
            </CardSub>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={clientExportRows.length === 0}
                >
                  <Download size={12} className="text-fg-dim" />
                  Download
                  <ChevronDown size={11} className="text-fg-mute" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={downloadClientsCsv}>
                  <FileText size={12} className="mr-1 text-fg-dim" />
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => void downloadClientsXlsx()}
                  disabled={exportingXlsx}
                >
                  <FileSpreadsheet size={12} className="mr-1 text-fg-dim" />
                  {exportingXlsx ? "Generating XLSX..." : "XLSX"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="ghost" onClick={onRefresh}>
              <RefreshCw
                size={12}
                className={cn("text-fg-dim", refreshing && "animate-spin")}
              />
              Refresh
            </Button>
          </div>
        </div>
        <DataTable
          columns={cols}
          data={filtered}
          rowKey={(r) => r.id}
          showTotal={false}
          toolbar={
            <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="font-mono text-[11px] text-fg-mute">
                {filtered.length} / {totalShown} clients
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-mute">
                  Filters
                </span>
                <Button
                  size="sm"
                  variant={onlineOnly ? "primary" : "ghost"}
                  onClick={() => setFilters({ onlineOnly: !onlineOnly })}
                >
                  Online only
                </Button>
                <Button
                  size="sm"
                  variant={heavyOnly ? "primary" : "ghost"}
                  onClick={() => setFilters({ heavyOnly: !heavyOnly })}
                >
                  Heavy users
                </Button>
                <Button
                  size="sm"
                  variant={wiredOnly ? "primary" : "ghost"}
                  onClick={() => setFilters({ wiredOnly: !wiredOnly })}
                >
                  Wired
                </Button>
                <Button
                  size="sm"
                  variant={edgeControllersOnly ? "primary" : "ghost"}
                  onClick={() => setFilters({ edgeControllersOnly: !edgeControllersOnly })}
                >
                  Edge Controllers
                </Button>
              </div>
            </div>
          }
        />
      </CardContent>
    </Card>
  );
}
